<template>
  <div class="px-4 py-4 max-w-screen-xl m-auto">
    <ReviewShowcaseTitle
      :average-rating="averageRating"
      :review-count="reviewCount"
      class="mb-8"
    />
    <div class="flex flex-row overflow-hidden justify-center">
      <ErrorBoundary>
        <component
          :is="wrapperComponent"
          data-test-id="review-showcase-container"
          :items-length="amountOfReviewsToRender"
          :class="{
            'flex flex-col gap-4': isMobile,
            'max-w-[780px] flex justify-center': amountOfReviewsToRender === 1,
          }"
          :hide-navigation="computedReviews.length <= 3"
          @scroll:right="carouselIdx = carouselIdx + 1"
          @scroll:left="carouselIdx--"
        >
          <ReviewCard
            v-for="i in amountOfReviewsToRender"
            :key="i"
            data-test-id="review-card-1"
            v-bind="getReviewCardProps[i - 1]"
            @click:image="clickOnImage(getReviewCardProps[i - 1].id)"
          >
            <template #title>
              <div class="flex">
                <div
                  class="text-sm md:text-base font-semibold text-black"
                  data-test-id="review-title"
                >
                  {{ getReviewCardProps[i - 1].reviewerName }}
                </div>
              </div>
            </template>

            <template #footer="{ reviewedAt }">
              <div class="text-xs" data-test-id="review-created-at">
                {{ reviewedAt }}
              </div>
            </template>
          </ReviewCard>
        </component>
      </ErrorBoundary>
    </div>

    <div v-if="computedReviews.length > 6" class="justify-center flex">
      <Button
        class="font-semibold max-w-[368px] w-full py-3 md:py-2 text-sm md:text-base"
        @click="isSidebarOpen = true"
      >
        {{ t('review.seeAll') }}
      </Button>
    </div>

    <ReviewSidebar
      :is-open="isSidebarOpen"
      :reviews="computedReviews"
      :average-rating="averageRating"
      :review-count="reviewCount"
      :selected-review-id="selectedReviewId"
      @close="closeSidebar"
      @update:selected-review-id="selectedReviewId = $event"
    />
  </div>
</template>
<script lang="ts" setup>
import dayjs from 'dayjs'
import type { Review } from '~/composables/types/event'

const { t, locale } = useI18n()
const { isMobile } = useDevice()

const {
  reviews = [],
  reviewCount = 0,
  averageRating = '',
  displayItemTitle = false,
} = defineProps<{
  reviews: Review[]
  averageRating: string
  reviewCount: number
  supplierName: string
  supplierCreatedAt: string
  displayItemTitle: boolean
}>()

const isSidebarOpen = ref(false)
const selectedReviewId = ref(undefined)
const carouselIdx = ref(0)

const clickOnImage = (id: string) => {
  selectedReviewId.value = id
  isSidebarOpen.value = true
}

const closeSidebar = () => {
  isSidebarOpen.value = false
  selectedReviewId.value = undefined
}

const computedReviews = computed(
  () =>
    [...reviews]?.sort((a, b) => {
      return dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? 1 : -1
    }) || []
)

const computedReviewsList = computed(() => {
  const length = computedReviews.value.length

  if (isMobile) return [...computedReviews.value].splice(0, 5)

  const adjustedIdx = (idx: number) => (length + (idx % length)) % length

  return [
    computedReviews.value[adjustedIdx(0 + carouselIdx.value)],
    computedReviews.value[adjustedIdx(1 + carouselIdx.value)],
    computedReviews.value[adjustedIdx(2 + carouselIdx.value)],
  ]
})

const amountOfReviewsToRender = computed(() =>
  Math.min(computedReviews.value.length, computedReviewsList.value.length)
)

const getReviewCardProps = computed(() =>
  computedReviewsList.value.map((review, i) => ({
    id: review.id,
    enableStars: false,
    averageRating: review.rating,
    reviewerName: review.customer,
    isTeamevent: false,
    reviewedAt: dateToFormat(review.created_at, 'MMMM D, YYYY', locale.value),
    reviewContent: review.review,
    images: review.media?.data,
    class: {
      'min-[1000px]:w-full  min-[1000px]:block': true,
      hidden: i > 0 && !isMobile,
      'md:min-h-[295px]': review.media?.data.length > 0,
      'md:min-h-[210px]': !review.media || review.media?.data.length === 0,
    },
    ...(review.item && displayItemTitle ? { itemTitle: review.item } : {}),
  }))
)

const wrapperComponent = computed(() =>
  isMobile && computedReviews.value.length > 3
    ? 'div'
    : resolveComponent('HorizontalCarouselWrapper')
)
</script>

<style scoped>
svg {
  display: inline;
}
</style>
